import {
  PEOPLE_CATEGORIES,
  PEOPLE_CHECKS,
  type PeopleCategoryId,
  type PeopleCheckId,
} from "./people-registry";
import { PEOPLE_READY_VERSION, type CheckResult, type ReportSnapshot, type Verification } from "./types";

export const PEOPLE_COVERAGE_THRESHOLD = 0.5;
export const PEOPLE_MIN_CONCLUSIVE = 2;

export type PeopleCheckState = "verified" | "missing" | "not_verified" | "not_applicable";

export type PeopleCategoryScore = {
  id: PeopleCategoryId;
  label: string;
  score: number | null;
  rawScore?: number | null;
  coverage: number;
  conclusive: number;
  applicable: number;
};

export function peopleCheckState(check: Pick<CheckResult, "status" | "verification">): PeopleCheckState {
  if (check.verification === "not_applicable" || check.status === "not_applicable") {
    return "not_applicable";
  }
  if (check.verification === "verified") return "verified";
  if (check.verification === "not_verified" || check.status === "not_tested") return "not_verified";
  if (check.status === "failed" || check.verification === "not_detected") return "missing";
  if (check.status === "passed") return "verified";
  return "not_verified";
}

export function isPeopleReadyV2(report: Pick<ReportSnapshot, "version"> | null | undefined): boolean {
  return report?.version === PEOPLE_READY_VERSION;
}

export function peopleReadyChecks(checks: CheckResult[]): CheckResult[] {
  const ids = new Set<string>(PEOPLE_CHECKS.map((check) => check.id));
  return checks.filter((check) => ids.has(check.id));
}

export function legacyEngagementChecks(checks: CheckResult[]): CheckResult[] {
  const ids = new Set<string>(PEOPLE_CHECKS.map((check) => check.id));
  return checks.filter((check) => !ids.has(check.id));
}

function weightOf(check: CheckResult): number {
  return check.weight > 0 ? check.weight : 1;
}

export function scorePeopleCategory(checks: CheckResult[], categoryId: PeopleCategoryId): PeopleCategoryScore {
  const label = PEOPLE_CATEGORIES.find((category) => category.id === categoryId)?.label ?? categoryId;
  const categoryChecks = peopleReadyChecks(checks).filter((check) => {
    const definition = PEOPLE_CHECKS.find((item) => item.id === check.id);
    return definition?.categoryId === categoryId;
  });
  let applicableWeight = 0;
  let conclusiveWeight = 0;
  let passedWeight = 0;
  let conclusive = 0;
  let applicable = 0;

  for (const check of categoryChecks) {
    const state = peopleCheckState(check);
    if (state === "not_applicable") continue;
    const weight = weightOf(check);
    applicable += 1;
    applicableWeight += weight;
    if (state === "not_verified") continue;
    conclusive += 1;
    conclusiveWeight += weight;
    if (state === "verified") passedWeight += weight;
  }

  const coverage = applicableWeight === 0 ? 0 : conclusiveWeight / applicableWeight;
  const enough = coverage >= PEOPLE_COVERAGE_THRESHOLD && conclusive >= PEOPLE_MIN_CONCLUSIVE;
  const rawScore = conclusiveWeight === 0 || !enough ? null : (passedWeight / conclusiveWeight) * 100;
  const score = rawScore == null ? null : Math.round(rawScore);

  return { id: categoryId, label, score, rawScore, coverage, conclusive, applicable };
}

export function scorePeopleReady(checks: CheckResult[]): {
  score: number | null;
  rawScore: number | null;
  coverage: number;
  categories: PeopleCategoryScore[];
  insufficientEvidence: boolean;
} {
  const categories = PEOPLE_CATEGORIES.map((category) => scorePeopleCategory(checks, category.id));
  const eligible = categories.filter((category) => category.score != null);
  const coverage =
    categories.reduce((sum, category) => sum + category.coverage, 0) / Math.max(categories.length, 1);
  if (eligible.length === 0) {
    return { score: null, rawScore: null, coverage, categories, insufficientEvidence: true };
  }
  const rawScore = eligible.reduce((sum, category) => sum + (category.rawScore ?? category.score ?? 0), 0) / eligible.length;
  return {
    score: Math.round(rawScore),
    rawScore,
    coverage,
    categories,
    insufficientEvidence: false,
  };
}

export function peopleStateToVerification(state: PeopleCheckState): Verification {
  if (state === "verified") return "verified";
  if (state === "missing") return "not_detected";
  if (state === "not_applicable") return "not_applicable";
  return "not_verified";
}

export function peopleStateToStatus(state: PeopleCheckState): CheckResult["status"] {
  if (state === "verified") return "passed";
  if (state === "missing") return "failed";
  if (state === "not_applicable") return "not_applicable";
  return "not_tested";
}

export function buildPeopleReport(
  checks: CheckResult[],
  extras?: { industry?: string },
): ReportSnapshot {
  const scored = scorePeopleReady(checks);
  return {
    version: PEOPLE_READY_VERSION,
    score: scored.score,
    coverage: {
      checked: peopleReadyChecks(checks).length,
      applicable: scored.categories.reduce((sum, category) => sum + category.applicable, 0),
      notTested: peopleReadyChecks(checks).filter((check) => peopleCheckState(check) === "not_verified").length,
      notApplicable: peopleReadyChecks(checks).filter((check) => peopleCheckState(check) === "not_applicable")
        .length,
    },
    categories: scored.categories.map((category) => ({
      category: category.label,
      score: category.score,
      passed: peopleReadyChecks(checks).filter(
        (check) =>
          PEOPLE_CHECKS.find((item) => item.id === (check.id as PeopleCheckId))?.categoryId === category.id &&
          peopleCheckState(check) === "verified",
      ).length,
      failed: peopleReadyChecks(checks).filter(
        (check) =>
          PEOPLE_CHECKS.find((item) => item.id === (check.id as PeopleCheckId))?.categoryId === category.id &&
          peopleCheckState(check) === "missing",
      ).length,
      warnings: 0,
      notApplicable: peopleReadyChecks(checks).filter(
        (check) =>
          PEOPLE_CHECKS.find((item) => item.id === (check.id as PeopleCheckId))?.categoryId === category.id &&
          peopleCheckState(check) === "not_applicable",
      ).length,
      notTested: peopleReadyChecks(checks).filter(
        (check) =>
          PEOPLE_CHECKS.find((item) => item.id === (check.id as PeopleCheckId))?.categoryId === category.id &&
          peopleCheckState(check) === "not_verified",
      ).length,
    })),
    checks,
    ...(extras?.industry ? { industry: extras.industry } : {}),
  };
}

export function mergePeopleAndLegacy(
  people: ReportSnapshot,
  legacy: ReportSnapshot,
): ReportSnapshot {
  return {
    ...people,
    version: PEOPLE_READY_VERSION,
    checks: [...peopleReadyChecks(people.checks), ...legacy.checks],
  };
}

export function rebuildPeopleV2Snapshot(checks: CheckResult[], industry?: string): ReportSnapshot {
  const people = peopleReadyChecks(checks);
  const legacy = legacyEngagementChecks(checks);
  const report = buildPeopleReport(people, industry ? { industry } : undefined);
  return { ...report, checks: [...people, ...legacy] };
}
